import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {
  Transition,
  Container,
  Grid,
  Segment,
  Header,
  Button,
  Icon,
  Label,
  Popup
} from 'semantic-ui-react';

import domains /* , { getS3Bucket } */ from '../../../containers/Domains';
import modes from './constants/modes';
import errors from './constants/errors';

import UploaderAssetPreview from './UploaderAssetPreview';
import UploaderAssetSummary from './UploaderAssetSummary';
import AssetTagSelection from './AssetTagSelection';

import { uploadFile } from '../../../fileUploader/FileUploader.v2';

class UploaderPanel extends PureComponent {
  constructor(props) {
    super(props);

    const { getFbToken, user } = props;

    this.state = {
      selectedTagOptions: {},
      selectedFiles: [], // refer this.onFileRead() for structure.
      processedFiles: [], // This will contain the list of file object with its status. Refer this.markUploadedFile() for the file structure.
      uploadStatus: modes.UPLOADER_PANEL.PENDING, // PENDING, UPLOADING, COMPLETED
      assetLibraryAccountId: domains.assetLibraryAccountId,
      fbToken: getFbToken(),
      currentUploadIndex: 0,
      tagOptions: {},
      user,
      requiredTagFields: ['oem', 'type', 'year', 'product', 'language'],

      // This prevents the user from uploading without selecting all neccessary tags. In updateSelectedTagOptions(),
      // the selected tags will be checked everytime the user selects a new tag and will assing this to true if all required tags are selected.
      allowToUpload: false,
      showRequiredTagWraning: false,
      highlightRequiredFields: false,
      reUploadRequired: false
    };
  }

  onFileSelect = (event) => {
    const { target } = event;
    const { files } = target;

    if (!files || files.length === 0) return;

    for (const file of files) {
      this.onFileRead(file);
    }

    if (this.fileInput) this.fileInput.value = null; // The value of the input has to be cleared otherwise, the user cannot select same file multiple times.
  };

  onFileRead = (file) => {
    const fileReader = new FileReader();

    fileReader.onload = (event) => {
      const { target } = event;
      const { result: dataUrl } = target;
      const { name, size } = file;

      this.setState((prevState) => ({
        ...prevState,
        selectedFiles: [
          ...prevState.selectedFiles,
          {
            key: `${name}_${size}`,
            file,
            dataUrl,
            status: modes.ASSET_FILE.UPLOAD_PENDING, // UPLOAD_PENDING, UPLOAD, COMPLETED, ERROR
            result: null
          }
        ]
      }));
    };

    fileReader.readAsDataURL(file);
  };

  onUploadButtonClick = () => {
    const { selectedFiles, user, allowToUpload } = this.state;
    const { displayError } = this.props; // this function is defined in AssetGalley.jsx

    const { email } = user || {};
    // check if the user is valid.
    if (!email) {
      displayError({
        type: errors.ASSET_GALLERY.INVALID_USER,
        title: 'Invalid User Info.',
        message: 'The user email address is not valid.'
      });

      return;
    }

    if (selectedFiles.length === 0) {
      displayError({
        type: errors.ASSET_GALLERY.NO_ASSET,
        title: 'No Asset is selected',
        message: 'At least one asset must be selected.'
      });
      return;
    }

    if (!allowToUpload) {
      // On second click, allow user to upload.
      this.setState((prevState) => ({
        ...prevState,
        allowToUpload: true,
        showRequiredTagWraning: true,
        highlightRequiredFields: true
      }));

      return;
    }

    const { updateUploaderStatus } = this.props;

    updateUploaderStatus(true, [], () => {
      const statusUpdatedFiles = selectedFiles.map((file) => ({
        ...file,
        status: modes.ASSET_FILE.UPLOAD_PENDING
      }));

      this.setState(
        {
          ...this.state,
          uploadStatus: modes.UPLOADER_PANEL.UPLOADING,
          selectedFiles: statusUpdatedFiles
        },
        () => {
          const { selectedFiles: updatedList, currentUploadIndex } = this.state;
          const { fbWorker } = this.props;

          if (!fbWorker) {
            throw new Error(
              'fbWorker is not defined in UploaderPanel.jsx. Please check the uploading flows.'
            );
          }

          fbWorker.addEventListener('message', ({ data }) => {
            const { processedFiles } = this.state;
            const updatedFiles = this.updateFilesProcessedInBackground(
              data,
              processedFiles
            );

            this.setState((prevState) => ({
              ...prevState,
              processedFiles: updatedFiles
            }));
          });

          this.startUploading(updatedList[currentUploadIndex]);
        }
      );
    });
  };

  /**
   * data is returned data from fb.worker.js.
   * The value workerInProgress has to be updated in order to update the label
   * that shows the status of the background work.
   */
  updateFilesProcessedInBackground = (data, processedFiles) => {
    const { assetId } = data || {};

    const newProcessedFiles = processedFiles.map((file) => {
      const { asset } = file || {};
      const { id: existingAssetId } = asset || {};

      if (existingAssetId === assetId) {
        return {
          ...file,
          workerInProgress: false
        };
      }

      return file;
    });

    return newProcessedFiles;
  };

  startUploading = (currentFile) => {
    const { selectedFiles } = this.state;

    const newList = selectedFiles.map((file) => {
      if (file.key === currentFile.key) {
        return {
          ...file,
          status: modes.ASSET_FILE.UPLOADING
        };
      }

      return {
        ...file
      };
    });

    this.setState((prevState) => ({
      ...prevState,
      selectedFiles: newList
    }));
  };

  onResetTagClick = (event) => {
    event.preventDefault();

    this.setState((prevState) => ({
      ...prevState,
      selectedTagOptions: {}
    }));
  };

  onResetButtonClick = () => {
    this.setState((prevState) => ({
      ...prevState,
      uploadStatus: modes.UPLOADER_PANEL.PENDING,
      selectedFiles: [],
      processedFiles: [],
      currentUploadIndex: 0,
      selectedTagOptions: {},
      allowToUpload: false,
      showRequiredTagWraning: false,
      highlightRequiredFields: false,
      reUploadRequired: false
    }));
  };

  onReuploadButtonClick = () => {
    const { processedFiles, selectedFiles } = this.state;

    const fileNamesThatAreFailed = processedFiles
      .filter(
        (processedFile) => processedFile.status === modes.ASSET_FILE.ERROR
      )
      .map((processedFile) => processedFile.file.name);
    const filesToReupload = selectedFiles
      .filter((selectedFile) =>
        fileNamesThatAreFailed.includes(selectedFile.file.name)
      )
      .map((selectedFile) => ({
        ...selectedFile,
        status: modes.ASSET_FILE.UPLOAD_PENDING
      }));

    this.setState((prevState) => ({
      ...prevState,
      selectedFiles: filesToReupload,
      processedFiles: [],
      uploadStatus: modes.UPLOADER_PANEL.PENDING,
      currentUploadIndex: 0,
      allowToUpload: false,
      showRequiredTagWraning: false,
      highlightRequiredFields: false,
      reUploadRequired: false
    }));
  };

  onUploaderPanelClose = () => {
    const { toggleUploaderPanel } = this.props;

    if (toggleUploaderPanel) toggleUploaderPanel(false);
  };

  /**
   * NOTE
   *
   * This function will be duplicated here and in the AssetGallery.jsx, AssetGalleryNav.jsx
   */
  updateTagOptions = (tagOption, callback) => {
    const keys = Object.keys(tagOption);

    if (keys.length !== 1) {
      throw new Error(
        'The parameter has more or less keys than expected. Please check where it was called.'
      );
    }

    const key = keys[0];

    this.setState(
      {
        ...this.state,
        tagOptions: {
          ...this.state.tagOptions,
          [key]: tagOption[key]
        }
      },
      () => {
        if (callback) callback();
      }
    );
  };

  /**
   * @param {string} category
   * @param {number[]} optionKeys The tags ids that are selected from the dropdowns.
   *
   * NOTE
   *
   * the optionsKeys is the full set of selected tag ids.
   *
   * This function will be duplicated in AssetGalleryNav.jsx
   */
  updateSelectedTagOptions = (category, options) => {
    const { selectedTagOptions, requiredTagFields } = this.state;
    // check if all required tags were selected including the current parameter (category).
    // If yes, change allowToUpload to true.
    const currentSelectedTagKeys = [
      ...Object.keys(selectedTagOptions),
      category
    ];

    const allTagsSelected =
      requiredTagFields.filter(
        (field) => currentSelectedTagKeys.indexOf(field) === -1
      ).length === 0;

    this.setState((prevState) => ({
      ...prevState,
      allowToUpload: allTagsSelected,
      selectedTagOptions: {
        ...prevState.selectedTagOptions,
        [category]: options
      }
    }));
  };

  /**
   * This function will be called for whenever an upload function gets response from the server.
   * If not all files were processed, it steps onto the next one.
   * It checks if all selected files are processed (uploaded/failed).
   * Upon process complete, it will show the end result message and update the view.
   *
   * @param {object} uploadedFile
   * @memberof UploaderPanel
   *
   * NOTE
   *
   * In uploadedFile, the dataUrl will be removed. Not needed here.
   *
   *
   * TODO: updating the current file and checking can be improved with the currentUploadIndex. Do this when you have chance.
   */
  markUploadedFile = (processedFile, status) => {
    const { key: processedFileKey } = processedFile;

    // check if all files were processed.
    const { selectedFiles, currentUploadIndex, processedFiles } = this.state;

    const markedFileArray = selectedFiles.map((file) => {
      const { key } = file;
      if (key === processedFileKey) {
        return {
          ...file,
          status
        };
      }

      return {
        ...file
      };
    });

    const notProcessedFiles = markedFileArray.filter((file) => {
      const { status: existingStatus } = file;

      return (
        existingStatus !== modes.ASSET_FILE.COMPLETED &&
        existingStatus !== modes.ASSET_FILE.ERROR
      );
    });

    // Check duplicate because when user closes the panel while uploading,
    // the summary page gets messed up.
    // TODO: We need to make sure if we send neccessary requests only.
    const updatedProcessedFiles = processedFiles.find((file) => {
      const { key: existingKey } = file;
      const { key: newKey } = processedFile;

      return existingKey === newKey;
    })
      ? processedFiles
      : [
          ...processedFiles,
          {
            ...processedFile,
            status
          }
        ];

    if (
      notProcessedFiles.length === 0 ||
      currentUploadIndex === selectedFiles.length - 1
    ) {
      // all files are processed.
      // update the view accordingly.

      const uploadedAssets = updatedProcessedFiles
        .filter((file) => file && file.asset) // asset might be null if uploading failed.
        .map((file) => file.asset);

      const reUploadRequired =
        updatedProcessedFiles.filter(
          (proccessedFile) => proccessedFile.status === modes.ASSET_FILE.ERROR
        ).length > 0;
      this.setState(
        {
          ...this.state,
          selectedFiles: markedFileArray,
          uploadStatus: modes.UPLOADER_PANEL.COMPLETED,
          processedFiles: updatedProcessedFiles,
          reUploadRequired
        },
        () => {
          const { updateUploaderStatus, updateAssetList } = this.props;

          updateUploaderStatus(false, updatedProcessedFiles, () => {
            updateAssetList(uploadedAssets);
          });
        }
      );

      return;
    }

    this.setState(
      {
        ...this.state,
        selectedFiles: markedFileArray,
        processedFiles: updatedProcessedFiles,
        currentUploadIndex: currentUploadIndex + 1
      },
      () => {
        const {
          selectedFiles: updatedFiles,
          currentUploadIndex: nextUploadIndex
        } = this.state;

        this.startUploading(updatedFiles[nextUploadIndex]);
      }
    );
  };

  // key is the key that is being used to generate asset list.
  removeSelectedAsset = (key) => {
    const { selectedFiles } = this.state;

    const tmpSelectedFiles = [...selectedFiles];

    // _.remove() removes element in place.
    // It returns an array with elements that were removed.
    _.remove(tmpSelectedFiles, (file) => {
      const { key: tmpKey } = file;

      return key === tmpKey;
    });

    this.setState((prevState) => ({
      ...prevState,
      selectedFiles: tmpSelectedFiles
    }));
  };

  render() {
    const { openUploaderPanel, fbWorker, onTagAdd, displayError } = this.props;
    const {
      selectedFiles,
      uploadStatus,
      assetLibraryAccountId,
      fbToken,
      user,
      processedFiles,
      selectedTagOptions,
      tagOptions,
      requiredTagFields,
      showRequiredTagWraning,
      highlightRequiredFields,
      reUploadRequired
    } = this.state;

    const numberOfFilesInWorker = processedFiles.filter(
      (file) => file.workerInProgress
    ).length;

    const numberOfProcessedFiles = processedFiles.length;

    const disableUploadingField =
      uploadStatus === modes.UPLOADER_PANEL.UPLOADING ||
      uploadStatus === modes.UPLOADER_PANEL.COMPLETED;

    return (
      <Container
        fluid
        style={{
          height: '100%',
          position: 'absolute',
          display: openUploaderPanel ? 'block' : 'none',
          zIndex: 10
        }}
      >
        <Transition.Group
          animation="fade down"
          duration={400}
          style={{
            height: '100%',
            width: '100%'
          }}
        >
          {openUploaderPanel && (
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: openUploaderPanel
                  ? 'rgba(255, 255, 255, 0.5)'
                  : 'white'
              }}
            >
              <Segment
                floated="left"
                style={{ width: '60%', height: '100%', marginRight: 0 }}
              >
                <Container fluid style={{ height: '5em' }}>
                  <Header as="h1" content="Upload Asset" floated="left" />
                  <Button
                    basic
                    icon
                    floated="right"
                    onClick={this.onUploaderPanelClose}
                  >
                    <Icon name="close" />
                  </Button>
                </Container>
                <Grid
                  columns={2}
                  stackable
                  textAlign="left"
                  style={{ height: '100%' }}
                >
                  <Grid.Row>
                    <Grid.Column
                      width={7}
                      style={{ overflowY: 'auto', height: '33em' }}
                    >
                      <Container>
                        <Button
                          basic
                          color="black"
                          icon
                          fluid
                          disabled={disableUploadingField}
                          style={
                            uploadStatus === modes.UPLOADER_PANEL.UPLOADING
                              ? { border: '1px solid' }
                              : {
                                  position: 'relative'
                                }
                          }
                        >
                          <span>
                            Add new asset
                            <input
                              style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                top: 0,
                                left: 0,
                                opacity: 0,
                                zIndex: 10
                              }}
                              disabled={disableUploadingField}
                              type="file"
                              accept="image/*, video/*"
                              // hidden
                              multiple
                              ref={(element) => {
                                if (!this.fileInput) this.fileInput = element;
                              }}
                              onChange={this.onFileSelect}
                            />
                          </span>
                        </Button>

                        {selectedFiles && selectedFiles.length > 0 ? (
                          selectedFiles.map((selectedFile) => {
                            const { key, status } = selectedFile;

                            return (
                              <UploaderAssetPreview
                                key={`${key}`}
                                selectedFile={selectedFile}
                                uploadStatus={uploadStatus}
                                status={status}
                                assetLibraryAccountId={assetLibraryAccountId}
                                fbToken={fbToken}
                                user={user}
                                uploadFile={uploadFile}
                                removeSelectedAsset={this.removeSelectedAsset}
                                markUploadedFile={this.markUploadedFile}
                                selectedTagOptions={selectedTagOptions}
                                fbWorker={fbWorker}
                              />
                            );
                          })
                        ) : (
                          // No selected assets
                          <Header
                            icon
                            textAlign="center"
                            style={{
                              position: 'relative',
                              top: '50%',
                              transform: 'translate(0, 100%)'
                            }}
                          >
                            <Icon name="file image outline" />
                            <Header.Content>
                              No assets are selected
                            </Header.Content>
                          </Header>
                        )}
                      </Container>
                    </Grid.Column>
                    <Grid.Column
                      width={9}
                      textAlign="left"
                      style={{ overflowY: 'auto', height: '33em' }}
                    >
                      <Transition.Group animation="fade down" duration={500}>
                        {uploadStatus === modes.UPLOADER_PANEL.PENDING ? (
                          <Container fluid>
                            <AssetTagSelection
                              allowAdditions
                              styles={{
                                marginLeft: '0 !important',
                                marginRight: '0 !important'
                              }}
                              tagOptions={tagOptions}
                              updateTagOptions={this.updateTagOptions}
                              updateSelectedTagOptions={
                                this.updateSelectedTagOptions
                              }
                              user={user}
                              selectedTagOptions={selectedTagOptions}
                              onTagAdd={onTagAdd}
                              requiredTagFields={requiredTagFields}
                              highLightRequiredFields={highlightRequiredFields}
                              displayError={displayError}
                            />
                          </Container>
                        ) : (
                          <UploaderAssetSummary
                            processedFiles={processedFiles}
                            selectedTagOptions={selectedTagOptions}
                            numberOfSelectedFiles={selectedFiles.length}
                            numberOfFilesInWorker={numberOfFilesInWorker}
                          />
                        )}
                      </Transition.Group>

                      {uploadStatus === modes.UPLOADER_PANEL.PENDING && (
                        <div style={{ width: '100%' }}>
                          {showRequiredTagWraning && (
                            <Label color="orange" basic pointing="below">
                              Not all required tags were selected. Please click
                              on upload button again if you want to proceed.
                            </Label>
                          )}
                          <Button
                            basic
                            onClick={this.onUploadButtonClick}
                            color="black"
                            style={{
                              width: '90%',
                              boxSizing: 'border-box',
                              margin: 0,
                              display: 'inline-block'
                            }}
                          >
                            Upload assets
                          </Button>
                          <Popup
                            content="Clear Tags"
                            trigger={
                              <Button
                                icon="repeat"
                                basic
                                color="black"
                                style={{
                                  width: '10%',
                                  boxSizing: 'border-box',
                                  margin: 0,
                                  display: 'inline-block'
                                }}
                                onClick={this.onResetTagClick}
                              />
                            }
                          />
                        </div>
                      )}

                      {uploadStatus === modes.UPLOADER_PANEL.COMPLETED && (
                        <div>
                          <Button
                            color="blue"
                            fluid
                            onClick={this.onResetButtonClick}
                            disabled={
                              numberOfProcessedFiles ===
                                numberOfFilesInWorker &&
                              numberOfFilesInWorker > 0
                            }
                            style={{
                              marginTop: '1em'
                            }}
                          >
                            <Icon name="repeat" />
                          </Button>

                          {reUploadRequired && (
                            <Popup
                              content="Re-upload failed assets."
                              trigger={
                                <Button
                                  color="red"
                                  fluid
                                  onClick={this.onReuploadButtonClick}
                                  disabled={
                                    numberOfProcessedFiles ===
                                      numberOfFilesInWorker &&
                                    numberOfFilesInWorker > 0
                                  }
                                  style={{
                                    marginTop: '1em'
                                  }}
                                >
                                  <Icon name="upload" />
                                </Button>
                              }
                            />
                          )}
                        </div>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Segment>
            </div>
          )}
        </Transition.Group>
      </Container>
    );
  }
}
/* eslint react/forbid-prop-types: 0 */
UploaderPanel.propTypes = {
  getFbToken: PropTypes.func.isRequired, // this function is defined in App.js,
  user: PropTypes.object.isRequired,
  displayError: PropTypes.func.isRequired,
  openUploaderPanel: PropTypes.bool.isRequired,
  toggleUploaderPanel: PropTypes.func.isRequired,
  updateUploaderStatus: PropTypes.func.isRequired,
  tagOptions: PropTypes.object.isRequired,
  updateAssetList: PropTypes.func.isRequired
};

export default UploaderPanel;



// WEBPACK FOOTER //
// ./src/components/common/AssetGalleryV2/UploaderPanel.jsx